/**
 * DC Auth Header Utilities
 *
 * Computes X-DC-FRIEND JWT and X-DC-DATE headers for SOLTOKEN
 * authentication with the inSuite _rest_ proxy.
 *
 * Uses Web Crypto API (available in browsers, Electron, and mobile).
 *
 * @since 1.0.0
 */

const SOL_NAME = 'frontdeskplus'

function base64url(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf)
  let binary = ''
  for (const byte of bytes) {
    binary += String.fromCharCode(byte)
  }
  return btoa(binary).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_')
}

function base64urlStr(value: string): string {
  return btoa(value).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_')
}

/**
 * Compute the X-DC-FRIEND JWT header for SOLTOKEN authentication.
 */
export async function computeDcFriendHeader(solToken: string, date: string): Promise<string> {
  const encoder = new TextEncoder()

  const header = JSON.stringify({ typ: 'JWT', alg: 'HS256' })
  const payload = JSON.stringify({ date, name: SOL_NAME })

  const encodedHeader = base64urlStr(header)
  const encodedPayload = base64urlStr(payload)
  const token = `${encodedHeader}.${encodedPayload}`

  const key = await crypto.subtle.importKey(
    'raw',
    encoder.encode(solToken),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  )

  const signature = await crypto.subtle.sign('HMAC', key, encoder.encode(token))
  return `${token}.${base64url(signature)}`
}

/**
 * Build the canonical signed headers used by every direct Bridge transport.
 *
 * @since 1.0.0
 * @example
 * ```ts
 * const headers = await createDcAuthHeaders("secret");
 * ```
 */
export async function createDcAuthHeaders(solToken: string): Promise<Headers> {
  const date = new Date().toISOString()
  const headers = new Headers()
  headers.set('X-DC-FRIEND', await computeDcFriendHeader(solToken, date))
  headers.set('X-DC-DATE', date)
  return headers
}
